import { LOCALE, arCount, type PortalLang } from '@/lib/lang';

/**
 * The public tour pages' words, Hebrew first, Arabic and English beside it.
 * Arabic is Modern Standard Arabic, addressed in the plural like the Hebrew.
 * The privacy notice is versioned on the server (TOUR_CONSENT_VERSION in
 * packages/api/src/modules/housing/tour-booking.service.ts), the same wording
 * in each language (the language shown is stored with the consent): change
 * the notice's meaning in all three and bump that version together.
 */
export type TourLang = PortalLang;

/** "2 hours" in Arabic: the dual, then the plural for 3 to 10. */
const arHours = (h: number) => arCount(h, 'ساعة واحدة', 'ساعتين', 'ساعات', 'ساعة');

export interface TourStrings {
  dir: 'rtl' | 'ltr';
  locale: string;
  title: string;
  subtitle: string;
  unitType: string;
  unitTypeAny: string;
  chooseDay: string;
  chooseTime: string;
  full: string;
  taken: string;
  noSlotsTitle: string;
  noSlotsBody: string;
  yourDetails: string;
  name: string;
  email: string;
  emailHint: string;
  phone: string;
  optional: string;
  notice: (operator: string, pendingHours: number) => string;
  noticeLegal: string;
  privacyPolicy: (operator: string) => string;
  marketing: (operator: string) => string;
  submit: string;
  sending: string;
  sentTitle: string;
  sentBody: (email: string, pendingHours: number) => string;
  sentSpam: string;
  notFoundTitle: string;
  notFoundBody: string;
  loadError: string;
  retry: string;
  errors: Record<string, string>;
  required: string;
  badEmail: string;
  badPhone: string;
  changeTime: string;
  // The manage link
  manageTitle: string;
  pendingBody: string;
  confirm: string;
  confirming: string;
  confirmedTitle: string;
  confirmedBody: string;
  cancel: string;
  cancelAsk: string;
  cancelYes: string;
  cancelNo: string;
  cancelledTitle: string;
  cancelledBody: string;
  expiredTitle: string;
  expiredBody: string;
  pastTitle: string;
  pastBody: string;
  bookAgain: string;
  linkInvalidTitle: string;
  linkInvalidBody: string;
  when: string;
  what: string;
}

export const TOUR_STRINGS: Record<TourLang, TourStrings> = {
  he: {
    dir: 'rtl',
    locale: LOCALE.he,
    title: 'קביעת סיור',
    subtitle: 'בחרו מועד, ונשלח לכם מייל לאישור הסיור.',
    unitType: 'איזו דירה מעניינת אתכם?',
    unitTypeAny: 'עוד לא יודע/ת',
    chooseDay: 'באיזה יום?',
    chooseTime: 'באיזו שעה?',
    full: 'מלא',
    taken: 'תפוס',
    noSlotsTitle: 'אין כרגע מועדים פנויים',
    noSlotsBody: 'כל המועדים בשבועיים הקרובים תפוסים. נסו שוב בעוד כמה ימים.',
    yourDetails: 'הפרטים שלכם',
    name: 'שם מלא',
    email: 'מייל',
    emailHint: 'לשם נשלח את קישור האישור',
    phone: 'טלפון',
    optional: 'לא חובה',
    notice: (operator, hours) =>
      `${operator} אוסף/ת את הפרטים כדי לתאם איתכם את הסיור. ${operator} הוא בעל המאגר והאחראי לפרטים, ו-TendedLoop מעבדת אותם עבורו. הטלפון וסוג הדירה אינם חובה. בלי אישור בקישור שבמייל תוך ${hours} שעות הבקשה נמחקת, ואם לא תתקדמו לחוזה הפרטים יימחקו אחרי 90 יום. נשלח לכם רק הודעות על הסיור הזה. רק אם סימנתם עדכונים: כדי למדוד את המודעות שלו, ${operator} עשוי/ה לדווח ל-Meta על הבקשה, עם כתובת ה-IP, סוג הדפדפן והמייל והטלפון בצורה מגובבת (hash) שאי אפשר לקרוא. בלי הסימון, שום דבר לא נשלח ל-Meta.`,
    noticeLegal: 'אין חובה חוקית למסור את הפרטים, אבל בלי שם ומייל לא נוכל לקבוע את הסיור.',
    privacyPolicy: (operator) => `מדיניות הפרטיות של ${operator}`,
    marketing: (operator) => `אשמח לקבל מ${operator} עדכונים על דירות פנויות במייל (לא חובה)`,
    submit: 'קביעת הסיור',
    sending: 'שולחים…',
    sentTitle: 'בדקו את המייל',
    sentBody: (email, hours) => `שלחנו קישור לאישור הסיור אל ${email}. המועד שמור לכם ${hours} שעות, עד שתאשרו.`,
    sentSpam: 'לא הגיע? בדקו בתיקיית הספאם או בקידומי מכירות.',
    notFoundTitle: 'הדף אינו זמין',
    notFoundBody: 'אין כאן דף לקביעת סיור. בדקו את הקישור או פנו למשרד.',
    loadError: 'לא הצלחנו לטעון את הדף. בדקו את החיבור ונסו שוב.',
    retry: 'נסו שוב',
    errors: {
      TOUR_SLOT_TAKEN: 'המועד הזה נתפס הרגע. בחרו מועד אחר.',
      TOUR_SLOT_UNAVAILABLE: 'המועד הזה כבר לא זמין. בחרו מועד אחר.',
      TOUR_NOTICE_CHANGED: 'הודעת הפרטיות עודכנה. קראו אותה שוב ושלחו.',
      TOUR_BOT_CHECK: 'בדיקת האבטחה לא עברה. נסו שוב.',
      RATE_LIMITED: 'יותר מדי ניסיונות. נסו שוב מאוחר יותר.',
      TOUR_EXPIRED: 'הבקשה פגה. אפשר לקבוע סיור חדש.',
      TOUR_NOT_CANCELLABLE: 'אי אפשר לבטל את הסיור הזה דרך הקישור. פנו למשרד.',
      DEFAULT: 'משהו השתבש. נסו שוב.',
    },
    required: 'שדה חובה',
    badEmail: 'כתובת המייל לא נראית תקינה',
    badPhone: 'מספרים בלבד',
    changeTime: 'שינוי מועד',
    manageTitle: 'הסיור שלכם',
    pendingBody: 'הסיור עוד לא אושר. לחצו כדי לשמור את המועד.',
    confirm: 'אישור הסיור',
    confirming: 'מאשרים…',
    confirmedTitle: 'הסיור נקבע',
    confirmedBody: 'נשלח אליכם מייל עם הפרטים. נשמח לראותכם.',
    cancel: 'ביטול הסיור',
    cancelAsk: 'לבטל את הסיור?',
    cancelYes: 'כן, לבטל',
    cancelNo: 'לא, להשאיר',
    cancelledTitle: 'הסיור בוטל',
    cancelledBody: 'המועד התפנה. אפשר לקבוע מועד חדש בכל עת.',
    expiredTitle: 'הבקשה פגה',
    expiredBody: 'הסיור לא אושר בזמן ולכן לא נשמר.',
    pastTitle: 'הסיור כבר עבר',
    pastBody: 'לסיור נוסף, קבעו מועד חדש.',
    bookAgain: 'קביעת סיור חדש',
    linkInvalidTitle: 'הקישור אינו תקף',
    linkInvalidBody: 'ייתכן שהקישור חלקי או ישן. פתחו אותו שוב מהמייל, או פנו למשרד.',
    when: 'מתי',
    what: 'סוג דירה',
  },
  ar: {
    dir: 'rtl',
    locale: LOCALE.ar,
    title: 'حجز جولة',
    subtitle: 'اختاروا موعدًا، وسنرسل إليكم بريدًا إلكترونيًا لتأكيد الجولة.',
    unitType: 'أيّ شقة تهمّكم؟',
    unitTypeAny: 'لا أعرف بعد',
    chooseDay: 'في أيّ يوم؟',
    chooseTime: 'في أيّ ساعة؟',
    full: 'مكتمل',
    taken: 'محجوز',
    noSlotsTitle: 'لا توجد مواعيد متاحة حاليًا',
    noSlotsBody: 'كل المواعيد في الأسبوعين القادمين محجوزة. حاولوا مرة أخرى بعد بضعة أيام.',
    yourDetails: 'بياناتكم',
    name: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    emailHint: 'سنرسل رابط التأكيد إلى هذا العنوان',
    phone: 'الهاتف',
    optional: 'اختياري',
    notice: (operator, hours) =>
      `يجمع ${operator} هذه البيانات لتنسيق الجولة معكم. ${operator} هو صاحب قاعدة البيانات والمسؤول عن البيانات، وتعالجها TendedLoop نيابةً عنه. رقم الهاتف ونوع الشقة اختياريان. إذا لم تؤكدوا عبر الرابط في البريد الإلكتروني خلال ${arHours(hours)}، يُحذف الطلب، وإذا لم توقّعوا عقدًا، تُحذف بياناتكم بعد 90 يومًا. لن نرسل إليكم إلا رسائل عن هذه الجولة. فقط إذا وضعتم علامة في خانة التحديثات: لقياس إعلاناته، قد يُبلغ ${operator} شركة Meta عن الطلب، مع عنوان IP ونوع المتصفح، والبريد الإلكتروني ورقم الهاتف بصيغة مُرمّزة (hash) لا يمكن قراءتها. من دون هذه العلامة، لا يُرسَل أي شيء إلى Meta.`,
    noticeLegal: 'لا يُلزمكم القانون بتقديم هذه البيانات، لكن من دون الاسم والبريد الإلكتروني لا يمكننا حجز الجولة.',
    privacyPolicy: (operator) => `سياسة الخصوصية لدى ${operator}`,
    marketing: (operator) => `يسعدني أن أتلقى من ${operator} تحديثات عن الشقق المتاحة عبر البريد الإلكتروني (اختياري)`,
    submit: 'حجز الجولة',
    sending: 'جارٍ الإرسال…',
    sentTitle: 'تفقّدوا بريدكم الإلكتروني',
    sentBody: (email, hours) =>
      `أرسلنا رابطًا لتأكيد الجولة إلى ${email}. الموعد محجوز لكم لمدة ${arHours(hours)} إلى أن تؤكدوا.`,
    sentSpam: 'لم يصل؟ تفقّدوا مجلد الرسائل غير المرغوب فيها (Spam) أو العروض الترويجية.',
    notFoundTitle: 'الصفحة غير متاحة',
    notFoundBody: 'لا توجد هنا صفحة لحجز جولة. تحقّقوا من الرابط أو تواصلوا مع المكتب.',
    loadError: 'تعذّر تحميل الصفحة. تحقّقوا من الاتصال وحاولوا مرة أخرى.',
    retry: 'حاولوا مرة أخرى',
    errors: {
      TOUR_SLOT_TAKEN: 'هذا الموعد حُجز للتو. اختاروا موعدًا آخر.',
      TOUR_SLOT_UNAVAILABLE: 'هذا الموعد لم يعد متاحًا. اختاروا موعدًا آخر.',
      TOUR_NOTICE_CHANGED: 'تم تحديث إشعار الخصوصية. اقرؤوه مرة أخرى وأرسلوا.',
      TOUR_BOT_CHECK: 'تعذّر اجتياز فحص الأمان. حاولوا مرة أخرى.',
      RATE_LIMITED: 'محاولات كثيرة جدًا. حاولوا مرة أخرى لاحقًا.',
      TOUR_EXPIRED: 'انتهت صلاحية الطلب. يمكنكم حجز جولة جديدة.',
      TOUR_NOT_CANCELLABLE: 'لا يمكن إلغاء هذه الجولة عبر الرابط. تواصلوا مع المكتب.',
      DEFAULT: 'حدث خطأ ما. حاولوا مرة أخرى.',
    },
    required: 'حقل مطلوب',
    badEmail: 'عنوان البريد الإلكتروني لا يبدو صحيحًا',
    badPhone: 'أرقام فقط',
    changeTime: 'تغيير الموعد',
    manageTitle: 'جولتكم',
    pendingBody: 'لم تُؤكَّد الجولة بعد. اضغطوا للاحتفاظ بالموعد.',
    confirm: 'تأكيد الجولة',
    confirming: 'جارٍ التأكيد…',
    confirmedTitle: 'تم حجز الجولة',
    confirmedBody: 'سنرسل إليكم بريدًا إلكترونيًا بالتفاصيل. يسعدنا لقاؤكم.',
    cancel: 'إلغاء الجولة',
    cancelAsk: 'هل تريدون إلغاء الجولة؟',
    cancelYes: 'نعم، إلغاء',
    cancelNo: 'لا، الإبقاء عليها',
    cancelledTitle: 'أُلغيت الجولة',
    cancelledBody: 'أصبح الموعد متاحًا. يمكنكم حجز موعد جديد في أي وقت.',
    expiredTitle: 'انتهت صلاحية الطلب',
    expiredBody: 'لم تُؤكَّد الجولة في الوقت المحدد، لذلك لم يُحفظ الموعد.',
    pastTitle: 'لقد مضى موعد الجولة',
    pastBody: 'لجولة أخرى، احجزوا موعدًا جديدًا.',
    bookAgain: 'حجز جولة جديدة',
    linkInvalidTitle: 'الرابط غير صالح',
    linkInvalidBody: 'قد يكون الرابط ناقصًا أو قديمًا. افتحوه مرة أخرى من البريد الإلكتروني، أو تواصلوا مع المكتب.',
    when: 'متى',
    what: 'نوع الشقة',
  },
  en: {
    dir: 'ltr',
    locale: LOCALE.en,
    title: 'Book a tour',
    subtitle: 'Pick a time and we will email you a link to confirm it.',
    unitType: 'Which home interests you?',
    unitTypeAny: 'Not sure yet',
    chooseDay: 'Which day?',
    chooseTime: 'What time?',
    full: 'Full',
    taken: 'Taken',
    noSlotsTitle: 'No times available right now',
    noSlotsBody: 'Every time in the next two weeks is taken. Please try again in a few days.',
    yourDetails: 'Your details',
    name: 'Full name',
    email: 'Email',
    emailHint: 'We send the confirmation link here',
    phone: 'Phone',
    optional: 'optional',
    notice: (operator, hours) =>
      `${operator} collects these details to arrange your tour. ${operator} is the controller of this data and TendedLoop processes it on their behalf. Phone and home type are optional. Without a confirmation from the emailed link within ${hours} hours the request is deleted, and if you do not go on to a lease your details are deleted after 90 days. We only send you messages about this tour. Only if you tick the news box: to measure its ads, ${operator} may tell Meta about this request, with your IP address, browser type, and your email and phone hashed in a form no one can read. Without the tick, nothing is sent to Meta.`,
    noticeLegal:
      'You are under no legal duty to give these details, but without a name and email we cannot book the tour.',
    privacyPolicy: (operator) => `${operator} privacy policy`,
    marketing: (operator) => `Email me news about available homes from ${operator} (optional)`,
    submit: 'Book the tour',
    sending: 'Sending…',
    sentTitle: 'Check your email',
    sentBody: (email, hours) =>
      `We sent a link to confirm the tour to ${email}. The time is held for you for ${hours} hours until you confirm.`,
    sentSpam: 'Not there? Check your spam or promotions folder.',
    notFoundTitle: 'This page is not available',
    notFoundBody: 'There is no tour booking page here. Check the link or contact the office.',
    loadError: 'We could not load the page. Check your connection and try again.',
    retry: 'Try again',
    errors: {
      TOUR_SLOT_TAKEN: 'That time was just taken. Please pick another.',
      TOUR_SLOT_UNAVAILABLE: 'That time is no longer offered. Please pick another.',
      TOUR_NOTICE_CHANGED: 'The privacy notice was updated. Please read it again and send.',
      TOUR_BOT_CHECK: 'The security check did not pass. Please try again.',
      RATE_LIMITED: 'Too many attempts. Please try again later.',
      TOUR_EXPIRED: 'This request expired. You can book a new tour.',
      TOUR_NOT_CANCELLABLE: 'This tour cannot be cancelled from the link. Please contact the office.',
      DEFAULT: 'Something went wrong. Please try again.',
    },
    required: 'Required',
    badEmail: 'This email does not look right',
    badPhone: 'Digits only',
    changeTime: 'Change time',
    manageTitle: 'Your tour',
    pendingBody: 'The tour is not confirmed yet. Confirm to keep the time.',
    confirm: 'Confirm the tour',
    confirming: 'Confirming…',
    confirmedTitle: 'Your tour is booked',
    confirmedBody: 'We emailed you the details. We look forward to seeing you.',
    cancel: 'Cancel the tour',
    cancelAsk: 'Cancel the tour?',
    cancelYes: 'Yes, cancel',
    cancelNo: 'No, keep it',
    cancelledTitle: 'Your tour is cancelled',
    cancelledBody: 'The time is free again. You can book a new one whenever you like.',
    expiredTitle: 'This request expired',
    expiredBody: 'The tour was not confirmed in time, so it was not kept.',
    pastTitle: 'This tour has passed',
    pastBody: 'For another tour, book a new time.',
    bookAgain: 'Book a new tour',
    linkInvalidTitle: 'This link is not valid',
    linkInvalidBody: 'The link may be cut off or old. Open it again from the email, or contact the office.',
    when: 'When',
    what: 'Home type',
  },
};

/** A language named by a link (`?lang=`, `#l=`), or null when it names none. */
export function tourLangOf(raw: string | null | undefined): TourLang | null {
  return raw === 'he' || raw === 'ar' || raw === 'en' ? raw : null;
}

/** The words of the signed-in portal's tour card and its booking sheet. */
export interface PortalTourStrings {
  checkFailed: string;
  retry: string;
  welcomeBook: string;
  render: string;
  inviteTitle: string;
  inviteBody: string;
  book: string;
  yourTour: string;
  at: string;
  officeToChange: string;
  cancel: string;
  move: string;
  sheetTitle: Record<'book' | 'move' | 'cancel', string>;
  close: string;
  /** Before the time, the time (set left to right), after it. */
  cancelBody: (day: string, time: string, canChange: boolean) => [string, string, string];
  cancelYes: string;
  cancelNo: string;
  now: string;
  currentLabel: string;
  booked: string;
  moved: string;
  pickTime: string;
  bookFor: (label: string) => string;
  moveTo: (label: string) => string;
  errors: Record<string, string>;
  failed: string;
  /** Between the day and the time (Arabic has its own comma). */
  comma: string;
  unitQuestion: string;
}

export const PORTAL_TOUR_STRINGS: Record<TourLang, PortalTourStrings> = {
  he: {
    checkFailed: 'לא הצלחנו לבדוק מועדים לסיור.',
    retry: 'נסו שוב',
    welcomeBook: 'קודם לראות את הדירה? קביעת סיור',
    render: 'הדמיה',
    inviteTitle: 'בואו לראות את הדירה לפני שמחליטים',
    inviteBody: 'סיור קצר בבניין, בזמן שנוח לך',
    book: 'קביעת סיור',
    yourTour: 'הסיור שלך',
    at: 'בשעה',
    officeToChange: 'לשינוי המועד, פנו למשרד.',
    cancel: 'ביטול הסיור',
    move: 'שינוי מועד',
    sheetTitle: { book: 'קביעת סיור', move: 'שינוי מועד הסיור', cancel: 'לבטל את הסיור?' },
    close: 'סגירה',
    cancelBody: (day, time, canChange) => [
      `הסיור ב${day} בשעה `,
      time,
      ` יבוטל, והמועד יתפנה. ${canChange ? 'אפשר לקבוע סיור חדש בכל עת.' : 'המשרד יקבל הודעה על הביטול.'}`,
    ],
    cancelYes: 'כן, לבטל את הסיור',
    cancelNo: 'לא, להשאיר',
    now: 'עכשיו:',
    currentLabel: 'המועד הנוכחי',
    booked: 'הסיור נקבע',
    moved: 'המועד עודכן',
    pickTime: 'בחרו שעה',
    bookFor: (label) => `קביעת הסיור ל${label}`,
    moveTo: (label) => `העברה ל${label}`,
    errors: {
      TOUR_SLOT_TAKEN: 'המועד הזה נתפס הרגע. בחרו מועד אחר.',
      TOUR_SLOT_UNAVAILABLE: 'המועד הזה כבר לא זמין. בחרו מועד אחר.',
      TOUR_ALREADY_BOOKED: 'כבר יש לך סיור שנקבע.',
      TOUR_NOT_FOUND_UPCOMING: 'הסיור הזה כבר לא פעיל.',
      TOUR_CHANGES_CLOSED: 'אי אפשר לשנות את המועד כרגע. לשינוי, פנו למשרד.',
      RATE_LIMIT_EXCEEDED: 'יותר מדי ניסיונות. נסו שוב בעוד כמה דקות.',
    },
    failed: 'משהו השתבש. נסו שוב.',
    comma: ', ',
    unitQuestion: 'איזו דירה מעניינת אותך?',
  },
  ar: {
    checkFailed: 'تعذّر التحقق من مواعيد الجولات.',
    retry: 'حاولوا مرة أخرى',
    welcomeBook: 'تودّون رؤية الشقة أولًا؟ احجزوا جولة',
    render: 'صورة توضيحية',
    inviteTitle: 'تعالوا لرؤية الشقة قبل أن تقرروا',
    inviteBody: 'جولة قصيرة في المبنى، في الوقت المناسب لكم',
    book: 'حجز جولة',
    yourTour: 'جولتكم',
    at: 'الساعة',
    officeToChange: 'لتغيير الموعد، تواصلوا مع المكتب.',
    cancel: 'إلغاء الجولة',
    move: 'تغيير الموعد',
    sheetTitle: { book: 'حجز جولة', move: 'تغيير موعد الجولة', cancel: 'هل تريدون إلغاء الجولة؟' },
    close: 'إغلاق',
    cancelBody: (day, time, canChange) => [
      `ستُلغى الجولة يوم ${day} الساعة `,
      time,
      `، ويصبح الموعد متاحًا. ${canChange ? 'يمكنكم حجز جولة جديدة في أي وقت.' : 'سيصل إلى المكتب إشعار بالإلغاء.'}`,
    ],
    cancelYes: 'نعم، إلغاء الجولة',
    cancelNo: 'لا، الإبقاء عليها',
    now: 'الموعد الحالي:',
    currentLabel: 'الموعد الحالي',
    booked: 'تم حجز الجولة',
    moved: 'تم تغيير الموعد',
    pickTime: 'اختاروا ساعة',
    bookFor: (label) => `حجز الجولة ليوم ${label}`,
    moveTo: (label) => `النقل إلى يوم ${label}`,
    errors: {
      TOUR_SLOT_TAKEN: 'هذا الموعد حُجز للتو. اختاروا موعدًا آخر.',
      TOUR_SLOT_UNAVAILABLE: 'هذا الموعد لم يعد متاحًا. اختاروا موعدًا آخر.',
      TOUR_ALREADY_BOOKED: 'لديكم جولة محجوزة بالفعل.',
      TOUR_NOT_FOUND_UPCOMING: 'هذه الجولة لم تعد فعّالة.',
      TOUR_CHANGES_CLOSED: 'لا يمكن تغيير الموعد حاليًا. للتغيير، تواصلوا مع المكتب.',
      RATE_LIMIT_EXCEEDED: 'محاولات كثيرة جدًا. حاولوا مرة أخرى بعد بضع دقائق.',
    },
    failed: 'حدث خطأ ما. حاولوا مرة أخرى.',
    comma: '، ',
    unitQuestion: 'أيّ شقة تهمّكم؟',
  },
  en: {
    checkFailed: 'We could not check tour times.',
    retry: 'Try again',
    welcomeBook: 'See the home first? Book a tour',
    render: 'Illustration',
    inviteTitle: 'Come and see the home before you decide',
    inviteBody: 'A short tour of the building, at a time that suits you',
    book: 'Book a tour',
    yourTour: 'Your tour',
    at: 'at',
    officeToChange: 'To change the time, contact the office.',
    cancel: 'Cancel the tour',
    move: 'Change time',
    sheetTitle: { book: 'Book a tour', move: 'Change the tour time', cancel: 'Cancel the tour?' },
    close: 'Close',
    cancelBody: (day, time, canChange) => [
      `Your tour on ${day} at `,
      time,
      ` will be cancelled and the time freed. ${canChange ? 'You can book a new tour whenever you like.' : 'The office will be told.'}`,
    ],
    cancelYes: 'Yes, cancel the tour',
    cancelNo: 'No, keep it',
    now: 'Now:',
    currentLabel: 'Current time',
    booked: 'Your tour is booked',
    moved: 'Time updated',
    pickTime: 'Pick a time',
    bookFor: (label) => `Book for ${label}`,
    moveTo: (label) => `Move to ${label}`,
    errors: {
      TOUR_SLOT_TAKEN: 'That time was just taken. Please pick another.',
      TOUR_SLOT_UNAVAILABLE: 'That time is no longer offered. Please pick another.',
      TOUR_ALREADY_BOOKED: 'You already have a tour booked.',
      TOUR_NOT_FOUND_UPCOMING: 'This tour is no longer active.',
      TOUR_CHANGES_CLOSED: 'The time cannot be changed right now. To change it, contact the office.',
      RATE_LIMIT_EXCEEDED: 'Too many attempts. Please try again in a few minutes.',
    },
    failed: 'Something went wrong. Please try again.',
    comma: ', ',
    unitQuestion: 'Which home interests you?',
  },
};

/** "Thursday, 5 November" and "16:00" in the company's zone. */
export function dayLabel(iso: string, timeZone: string, s: TourStrings): string {
  return new Intl.DateTimeFormat(s.locale, { timeZone, weekday: 'long', day: 'numeric', month: 'long' }).format(
    new Date(iso),
  );
}
export function timeLabel(iso: string, timeZone: string, s: TourStrings): string {
  return new Intl.DateTimeFormat(s.locale, { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    new Date(iso),
  );
}
